import React from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, StatusBadge, Text, Title } from '@/shared/ui';
import { VisitStatusBadge } from '@features/visits/components/VisitStatusBadge';
import type { VisitStatus } from '@features/visits/constants/visit-colors';
import type { Visit } from '@services/requests/housien-models/Visit';
import { getRelativeVisitTime } from '@/shared/utils/relative-visit-time';
import type { RelativeTimeLabels } from '@/shared/utils/relative-visit-time';
import {
  buildMetaChips,
  buildSubtitle,
  ClientInitials,
  RelativeTimeBadge,
} from './VisitDetailHeroCard.helpers';
import { useIsSoloBroker } from '@/shared/hooks/use-is-solo-broker';

interface VisitDetailHeroCardProps {
  visit: Visit;
  agencyId: string;
}

/**
 * Unified hero header card: avatar + identity + badges + relative time +
 * meta chips + edit action.
 */
export function VisitDetailHeroCard({ visit, agencyId }: VisitDetailHeroCardProps) {
  const { t } = useTranslation('@pages/visits');
  const tAgency = useTranslation('@pages/agencies').t;
  const isSoloBroker = useIsSoloBroker();
  const status = (visit.status ?? 'pending') as VisitStatus;
  const clientName = visit.client_name || tAgency('visits.detail.client');
  const clientId = visit.client_id;
  const clientHref = clientId ? `/app/agencies/${agencyId}/clients/${clientId}` : undefined;
  const subtitle = buildSubtitle(visit);
  const labels: RelativeTimeLabels = {
    yesterday: t('kanban.relativeTime.yesterday'),
    now: t('kanban.relativeTime.now'),
    tomorrow: t('kanban.relativeTime.tomorrow'),
    minutesAgo: (m) => t('kanban.relativeTime.minutesAgo', { count: m }),
    hoursAgo: (h) => t('kanban.relativeTime.hoursAgo', { count: h }),
    daysAgo: (d) => t('kanban.relativeTime.daysAgo', { count: d }),
    inMinutes: (m) => t('kanban.relativeTime.inMinutes', { count: m }),
    inHours: (h) => t('kanban.relativeTime.inHours', { count: h }),
    inDays: (d) => t('kanban.relativeTime.inDays', { count: d }),
  };

  const chips = buildMetaChips(visit, { showBroker: !isSoloBroker });
  const relativeTime = visit.visit_datetime
    ? getRelativeVisitTime(visit.visit_datetime, undefined, labels)
    : null;

  return (
    <div
      data-testid="visit-detail-header-card"
      className="shrink-0 rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-primary) shadow-[var(--shadow-md)] overflow-hidden mb-[var(--space-4)]"
    >
      <div className="p-[var(--space-5)] bg-[color-mix(in_srgb,var(--seed-base)_3%,var(--surface-primary))] border-b border-(--border-subtle)">
        <div className="flex items-start justify-between gap-[var(--space-4)]">
          <div className="flex items-center gap-[var(--space-4)]">
            <ClientInitials name={clientName} />
            <div className="min-w-0">
              {clientHref ? (
                <Anchor
                  href={clientHref}
                  data-testid="visit-detail-client-link"
                  underline="hover"
                  className="text-[length:var(--text-app-heading)] [font-weight:var(--font-bold)] text-[var(--text-primary)] m-0 min-w-0 leading-[1.25] no-underline hover:text-(--seed-base)"
                >
                  {clientName}
                </Anchor>
              ) : (
                <Title
                  order={2}
                  className="text-[length:var(--text-app-heading)] [font-weight:var(--font-bold)] text-[var(--text-primary)] m-0 min-w-0 leading-[1.25]"
                >
                  {clientName}
                </Title>
              )}
              {subtitle && (
                <Text className="text-[length:var(--text-body-sm)] text-[var(--text-tertiary)] m-0 mt-[var(--space-1)]">
                  {subtitle}
                </Text>
              )}
              <div className="flex items-center gap-[var(--space-2)] mt-[var(--space-2)] flex-wrap">
                <VisitStatusBadge status={status} />
                {relativeTime && (
                  <RelativeTimeBadge label={relativeTime.label} urgency={relativeTime.urgency} />
                )}
              </div>
            </div>
          </div>
        </div>

        {chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-(--space-2) mt-(--space-4)">
            {chips.map((chip) => (
              <StatusBadge key={chip.label} tone="neutral">
                <Text component="span" className="flex items-center gap-(--space-1)">
                  {chip.icon}
                  {chip.label}
                </Text>
              </StatusBadge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
